import { z } from 'zod';

const PhoneDialCodeSchema = z.string().regex(/^\+\d{1,4}$/);

export const PhoneCountryOptionSchema = z.object({
  name: z.string(),
  iso2: z.string().trim().length(2),
  phone: PhoneDialCodeSchema,
  flag: z.string(),
});

export type PhoneCountryOption = z.infer<typeof PhoneCountryOptionSchema>;

export interface SplitPhoneNumberOptions {
  preferredCountryIso?: string | null;
}

export interface SplitPhoneNumberResult {
  phoneCountry: PhoneCountryOption | null;
  phoneNumber: string;
}

const PHONE_COUNTRY_DEFINITIONS = [
  { name: 'Afghanistan', iso2: 'AF', phone: '+93' },
  { name: 'Åland Islands', iso2: 'AX', phone: '+358' },
  { name: 'Albania', iso2: 'AL', phone: '+355' },
  { name: 'Algeria', iso2: 'DZ', phone: '+213' },
  { name: 'American Samoa', iso2: 'AS', phone: '+1' },
  { name: 'Andorra', iso2: 'AD', phone: '+376' },
  { name: 'Angola', iso2: 'AO', phone: '+244' },
  { name: 'Anguilla', iso2: 'AI', phone: '+1' },
  { name: 'Antigua and Barbuda', iso2: 'AG', phone: '+1' },
  { name: 'Argentina', iso2: 'AR', phone: '+54' },
  { name: 'Armenia', iso2: 'AM', phone: '+374' },
  { name: 'Aruba', iso2: 'AW', phone: '+297' },
  { name: 'Australia', iso2: 'AU', phone: '+61' },
  { name: 'Austria', iso2: 'AT', phone: '+43' },
  { name: 'Azerbaijan', iso2: 'AZ', phone: '+994' },
  { name: 'Bahamas', iso2: 'BS', phone: '+1' },
  { name: 'Bahrain', iso2: 'BH', phone: '+973' },
  { name: 'Bangladesh', iso2: 'BD', phone: '+880' },
  { name: 'Barbados', iso2: 'BB', phone: '+1' },
  { name: 'Belarus', iso2: 'BY', phone: '+375' },
  { name: 'Belgium', iso2: 'BE', phone: '+32' },
  { name: 'Belize', iso2: 'BZ', phone: '+501' },
  { name: 'Benin', iso2: 'BJ', phone: '+229' },
  { name: 'Bermuda', iso2: 'BM', phone: '+1' },
  { name: 'Bhutan', iso2: 'BT', phone: '+975' },
  { name: 'Bolivia', iso2: 'BO', phone: '+591' },
  { name: 'Bonaire, Sint Eustatius and Saba', iso2: 'BQ', phone: '+599' },
  { name: 'Bosnia and Herzegovina', iso2: 'BA', phone: '+387' },
  { name: 'Botswana', iso2: 'BW', phone: '+267' },
  { name: 'Brazil', iso2: 'BR', phone: '+55' },
  { name: 'British Indian Ocean Territory', iso2: 'IO', phone: '+246' },
  { name: 'British Virgin Islands', iso2: 'VG', phone: '+1' },
  { name: 'Brunei', iso2: 'BN', phone: '+673' },
  { name: 'Bulgaria', iso2: 'BG', phone: '+359' },
  { name: 'Burkina Faso', iso2: 'BF', phone: '+226' },
  { name: 'Burundi', iso2: 'BI', phone: '+257' },
  { name: 'Cambodia', iso2: 'KH', phone: '+855' },
  { name: 'Cameroon', iso2: 'CM', phone: '+237' },
  { name: 'Canada', iso2: 'CA', phone: '+1' },
  { name: 'Cape Verde', iso2: 'CV', phone: '+238' },
  { name: 'Cayman Islands', iso2: 'KY', phone: '+1' },
  { name: 'Central African Republic', iso2: 'CF', phone: '+236' },
  { name: 'Chad', iso2: 'TD', phone: '+235' },
  { name: 'Chile', iso2: 'CL', phone: '+56' },
  { name: 'China', iso2: 'CN', phone: '+86' },
  { name: 'Colombia', iso2: 'CO', phone: '+57' },
  { name: 'Comoros', iso2: 'KM', phone: '+269' },
  { name: 'Congo', iso2: 'CG', phone: '+242' },
  { name: 'Congo (DRC)', iso2: 'CD', phone: '+243' },
  { name: 'Cook Islands', iso2: 'CK', phone: '+682' },
  { name: 'Costa Rica', iso2: 'CR', phone: '+506' },
  { name: "Côte d'Ivoire", iso2: 'CI', phone: '+225' },
  { name: 'Croatia', iso2: 'HR', phone: '+385' },
  { name: 'Cuba', iso2: 'CU', phone: '+53' },
  { name: 'Curaçao', iso2: 'CW', phone: '+599' },
  { name: 'Cyprus', iso2: 'CY', phone: '+357' },
  { name: 'Czechia', iso2: 'CZ', phone: '+420' },
  { name: 'Denmark', iso2: 'DK', phone: '+45' },
  { name: 'Djibouti', iso2: 'DJ', phone: '+253' },
  { name: 'Dominica', iso2: 'DM', phone: '+1' },
  { name: 'Dominican Republic', iso2: 'DO', phone: '+1' },
  { name: 'Ecuador', iso2: 'EC', phone: '+593' },
  { name: 'Egypt', iso2: 'EG', phone: '+20' },
  { name: 'El Salvador', iso2: 'SV', phone: '+503' },
  { name: 'Equatorial Guinea', iso2: 'GQ', phone: '+240' },
  { name: 'Eritrea', iso2: 'ER', phone: '+291' },
  { name: 'Estonia', iso2: 'EE', phone: '+372' },
  { name: 'Eswatini', iso2: 'SZ', phone: '+268' },
  { name: 'Ethiopia', iso2: 'ET', phone: '+251' },
  { name: 'Falkland Islands', iso2: 'FK', phone: '+500' },
  { name: 'Faroe Islands', iso2: 'FO', phone: '+298' },
  { name: 'Fiji', iso2: 'FJ', phone: '+679' },
  { name: 'Finland', iso2: 'FI', phone: '+358' },
  { name: 'France', iso2: 'FR', phone: '+33' },
  { name: 'French Guiana', iso2: 'GF', phone: '+594' },
  { name: 'French Polynesia', iso2: 'PF', phone: '+689' },
  { name: 'Gabon', iso2: 'GA', phone: '+241' },
  { name: 'Gambia', iso2: 'GM', phone: '+220' },
  { name: 'Georgia', iso2: 'GE', phone: '+995' },
  { name: 'Germany', iso2: 'DE', phone: '+49' },
  { name: 'Ghana', iso2: 'GH', phone: '+233' },
  { name: 'Gibraltar', iso2: 'GI', phone: '+350' },
  { name: 'Greece', iso2: 'GR', phone: '+30' },
  { name: 'Greenland', iso2: 'GL', phone: '+299' },
  { name: 'Grenada', iso2: 'GD', phone: '+1' },
  { name: 'Guadeloupe', iso2: 'GP', phone: '+590' },
  { name: 'Guam', iso2: 'GU', phone: '+1' },
  { name: 'Guatemala', iso2: 'GT', phone: '+502' },
  { name: 'Guernsey', iso2: 'GG', phone: '+44' },
  { name: 'Guinea', iso2: 'GN', phone: '+224' },
  { name: 'Guinea-Bissau', iso2: 'GW', phone: '+245' },
  { name: 'Guyana', iso2: 'GY', phone: '+592' },
  { name: 'Haiti', iso2: 'HT', phone: '+509' },
  { name: 'Honduras', iso2: 'HN', phone: '+504' },
  { name: 'Hong Kong', iso2: 'HK', phone: '+852' },
  { name: 'Hungary', iso2: 'HU', phone: '+36' },
  { name: 'Iceland', iso2: 'IS', phone: '+354' },
  { name: 'India', iso2: 'IN', phone: '+91' },
  { name: 'Indonesia', iso2: 'ID', phone: '+62' },
  { name: 'Iran', iso2: 'IR', phone: '+98' },
  { name: 'Iraq', iso2: 'IQ', phone: '+964' },
  { name: 'Ireland', iso2: 'IE', phone: '+353' },
  { name: 'Isle of Man', iso2: 'IM', phone: '+44' },
  { name: 'Israel', iso2: 'IL', phone: '+972' },
  { name: 'Italy', iso2: 'IT', phone: '+39' },
  { name: 'Jamaica', iso2: 'JM', phone: '+1' },
  { name: 'Japan', iso2: 'JP', phone: '+81' },
  { name: 'Jersey', iso2: 'JE', phone: '+44' },
  { name: 'Jordan', iso2: 'JO', phone: '+962' },
  { name: 'Kazakhstan', iso2: 'KZ', phone: '+7' },
  { name: 'Kenya', iso2: 'KE', phone: '+254' },
  { name: 'Kiribati', iso2: 'KI', phone: '+686' },
  { name: 'Kosovo', iso2: 'XK', phone: '+383' },
  { name: 'Kuwait', iso2: 'KW', phone: '+965' },
  { name: 'Kyrgyzstan', iso2: 'KG', phone: '+996' },
  { name: 'Laos', iso2: 'LA', phone: '+856' },
  { name: 'Latvia', iso2: 'LV', phone: '+371' },
  { name: 'Lebanon', iso2: 'LB', phone: '+961' },
  { name: 'Lesotho', iso2: 'LS', phone: '+266' },
  { name: 'Liberia', iso2: 'LR', phone: '+231' },
  { name: 'Libya', iso2: 'LY', phone: '+218' },
  { name: 'Liechtenstein', iso2: 'LI', phone: '+423' },
  { name: 'Lithuania', iso2: 'LT', phone: '+370' },
  { name: 'Luxembourg', iso2: 'LU', phone: '+352' },
  { name: 'Macau', iso2: 'MO', phone: '+853' },
  { name: 'Madagascar', iso2: 'MG', phone: '+261' },
  { name: 'Malawi', iso2: 'MW', phone: '+265' },
  { name: 'Malaysia', iso2: 'MY', phone: '+60' },
  { name: 'Maldives', iso2: 'MV', phone: '+960' },
  { name: 'Mali', iso2: 'ML', phone: '+223' },
  { name: 'Malta', iso2: 'MT', phone: '+356' },
  { name: 'Marshall Islands', iso2: 'MH', phone: '+692' },
  { name: 'Martinique', iso2: 'MQ', phone: '+596' },
  { name: 'Mauritania', iso2: 'MR', phone: '+222' },
  { name: 'Mauritius', iso2: 'MU', phone: '+230' },
  { name: 'Mayotte', iso2: 'YT', phone: '+262' },
  { name: 'Mexico', iso2: 'MX', phone: '+52' },
  { name: 'Micronesia', iso2: 'FM', phone: '+691' },
  { name: 'Moldova', iso2: 'MD', phone: '+373' },
  { name: 'Monaco', iso2: 'MC', phone: '+377' },
  { name: 'Mongolia', iso2: 'MN', phone: '+976' },
  { name: 'Montenegro', iso2: 'ME', phone: '+382' },
  { name: 'Montserrat', iso2: 'MS', phone: '+1' },
  { name: 'Morocco', iso2: 'MA', phone: '+212' },
  { name: 'Mozambique', iso2: 'MZ', phone: '+258' },
  { name: 'Myanmar', iso2: 'MM', phone: '+95' },
  { name: 'Namibia', iso2: 'NA', phone: '+264' },
  { name: 'Nauru', iso2: 'NR', phone: '+674' },
  { name: 'Nepal', iso2: 'NP', phone: '+977' },
  { name: 'Netherlands', iso2: 'NL', phone: '+31' },
  { name: 'New Caledonia', iso2: 'NC', phone: '+687' },
  { name: 'New Zealand', iso2: 'NZ', phone: '+64' },
  { name: 'Nicaragua', iso2: 'NI', phone: '+505' },
  { name: 'Niger', iso2: 'NE', phone: '+227' },
  { name: 'Nigeria', iso2: 'NG', phone: '+234' },
  { name: 'Niue', iso2: 'NU', phone: '+683' },
  { name: 'North Korea', iso2: 'KP', phone: '+850' },
  { name: 'North Macedonia', iso2: 'MK', phone: '+389' },
  { name: 'Northern Mariana Islands', iso2: 'MP', phone: '+1' },
  { name: 'Norway', iso2: 'NO', phone: '+47' },
  { name: 'Oman', iso2: 'OM', phone: '+968' },
  { name: 'Pakistan', iso2: 'PK', phone: '+92' },
  { name: 'Palau', iso2: 'PW', phone: '+680' },
  { name: 'Palestine', iso2: 'PS', phone: '+970' },
  { name: 'Panama', iso2: 'PA', phone: '+507' },
  { name: 'Papua New Guinea', iso2: 'PG', phone: '+675' },
  { name: 'Paraguay', iso2: 'PY', phone: '+595' },
  { name: 'Peru', iso2: 'PE', phone: '+51' },
  { name: 'Philippines', iso2: 'PH', phone: '+63' },
  { name: 'Poland', iso2: 'PL', phone: '+48' },
  { name: 'Portugal', iso2: 'PT', phone: '+351' },
  { name: 'Puerto Rico', iso2: 'PR', phone: '+1' },
  { name: 'Qatar', iso2: 'QA', phone: '+974' },
  { name: 'Réunion', iso2: 'RE', phone: '+262' },
  { name: 'Romania', iso2: 'RO', phone: '+40' },
  { name: 'Russia', iso2: 'RU', phone: '+7' },
  { name: 'Rwanda', iso2: 'RW', phone: '+250' },
  { name: 'Saint Barthélemy', iso2: 'BL', phone: '+590' },
  { name: 'Saint Helena', iso2: 'SH', phone: '+290' },
  { name: 'Saint Kitts and Nevis', iso2: 'KN', phone: '+1' },
  { name: 'Saint Lucia', iso2: 'LC', phone: '+1' },
  { name: 'Saint Martin', iso2: 'MF', phone: '+590' },
  { name: 'Saint Pierre and Miquelon', iso2: 'PM', phone: '+508' },
  { name: 'Saint Vincent and the Grenadines', iso2: 'VC', phone: '+1' },
  { name: 'Samoa', iso2: 'WS', phone: '+685' },
  { name: 'San Marino', iso2: 'SM', phone: '+378' },
  { name: 'São Tomé and Príncipe', iso2: 'ST', phone: '+239' },
  { name: 'Saudi Arabia', iso2: 'SA', phone: '+966' },
  { name: 'Senegal', iso2: 'SN', phone: '+221' },
  { name: 'Serbia', iso2: 'RS', phone: '+381' },
  { name: 'Seychelles', iso2: 'SC', phone: '+248' },
  { name: 'Sierra Leone', iso2: 'SL', phone: '+232' },
  { name: 'Singapore', iso2: 'SG', phone: '+65' },
  { name: 'Sint Maarten', iso2: 'SX', phone: '+1' },
  { name: 'Slovakia', iso2: 'SK', phone: '+421' },
  { name: 'Slovenia', iso2: 'SI', phone: '+386' },
  { name: 'Solomon Islands', iso2: 'SB', phone: '+677' },
  { name: 'Somalia', iso2: 'SO', phone: '+252' },
  { name: 'South Africa', iso2: 'ZA', phone: '+27' },
  { name: 'South Korea', iso2: 'KR', phone: '+82' },
  { name: 'South Sudan', iso2: 'SS', phone: '+211' },
  { name: 'Spain', iso2: 'ES', phone: '+34' },
  { name: 'Sri Lanka', iso2: 'LK', phone: '+94' },
  { name: 'Sudan', iso2: 'SD', phone: '+249' },
  { name: 'Suriname', iso2: 'SR', phone: '+597' },
  { name: 'Sweden', iso2: 'SE', phone: '+46' },
  { name: 'Switzerland', iso2: 'CH', phone: '+41' },
  { name: 'Syria', iso2: 'SY', phone: '+963' },
  { name: 'Taiwan', iso2: 'TW', phone: '+886' },
  { name: 'Tajikistan', iso2: 'TJ', phone: '+992' },
  { name: 'Tanzania', iso2: 'TZ', phone: '+255' },
  { name: 'Thailand', iso2: 'TH', phone: '+66' },
  { name: 'Timor-Leste', iso2: 'TL', phone: '+670' },
  { name: 'Togo', iso2: 'TG', phone: '+228' },
  { name: 'Tokelau', iso2: 'TK', phone: '+690' },
  { name: 'Tonga', iso2: 'TO', phone: '+676' },
  { name: 'Trinidad and Tobago', iso2: 'TT', phone: '+1' },
  { name: 'Tunisia', iso2: 'TN', phone: '+216' },
  { name: 'Turkey', iso2: 'TR', phone: '+90' },
  { name: 'Turkmenistan', iso2: 'TM', phone: '+993' },
  { name: 'Turks and Caicos Islands', iso2: 'TC', phone: '+1' },
  { name: 'Tuvalu', iso2: 'TV', phone: '+688' },
  { name: 'Uganda', iso2: 'UG', phone: '+256' },
  { name: 'Ukraine', iso2: 'UA', phone: '+380' },
  { name: 'United Arab Emirates', iso2: 'AE', phone: '+971' },
  { name: 'United Kingdom', iso2: 'GB', phone: '+44' },
  { name: 'United States', iso2: 'US', phone: '+1' },
  { name: 'Uruguay', iso2: 'UY', phone: '+598' },
  { name: 'Uzbekistan', iso2: 'UZ', phone: '+998' },
  { name: 'Vanuatu', iso2: 'VU', phone: '+678' },
  { name: 'Vatican City', iso2: 'VA', phone: '+379' },
  { name: 'Venezuela', iso2: 'VE', phone: '+58' },
  { name: 'Vietnam', iso2: 'VN', phone: '+84' },
  { name: 'Wallis and Futuna', iso2: 'WF', phone: '+681' },
  { name: 'Yemen', iso2: 'YE', phone: '+967' },
  { name: 'Zambia', iso2: 'ZM', phone: '+260' },
  { name: 'Zimbabwe', iso2: 'ZW', phone: '+263' },
] as const;

export const PROHIBITED_PHONE_COUNTRY_ISOS: ReadonlySet<string> = new Set([
  'AF', // Afghanistan
  'BY', // Belarus
  'BI', // Burundi
  'CF', // Central African Republic
  'CU', // Cuba
  'CD', // Congo (DRC)
  'ET', // Ethiopia
  'HT', // Haiti
  'IR', // Iran
  'IQ', // Iraq
  'IL', // Israel
  'KP', // North Korea
  'LB', // Lebanon
  'LY', // Libya
  'ML', // Mali
  'MM', // Myanmar
  'NI', // Nicaragua
  'NE', // Niger
  'PS', // Palestine
  'RU', // Russia
  'SO', // Somalia
  'SS', // South Sudan
  'SD', // Sudan
  'SY', // Syria
  'VE', // Venezuela
  'YE', // Yemen
  'ZW', // Zimbabwe
]);

export const toFlagEmoji = (iso2: string): string => {
  const normalized = iso2.trim().toUpperCase();

  if (!/^[A-Z]{2}$/.test(normalized)) {
    return '🏳️';
  }

  const regionalIndicatorOffset = 0x1f1e6;
  const first = normalized.charCodeAt(0) - 65 + regionalIndicatorOffset;
  const second = normalized.charCodeAt(1) - 65 + regionalIndicatorOffset;

  return String.fromCodePoint(first, second);
};

export const PHONE_COUNTRY_OPTIONS: ReadonlyArray<PhoneCountryOption> = Object.freeze(
  PHONE_COUNTRY_DEFINITIONS.filter(
    (country) => !PROHIBITED_PHONE_COUNTRY_ISOS.has(country.iso2.toUpperCase()),
  )
    .map((country) =>
      Object.freeze({
        ...country,
        iso2: country.iso2.toUpperCase(),
        flag: toFlagEmoji(country.iso2),
      }),
    )
    .sort((left, right) => left.name.localeCompare(right.name)),
);

export const getPhoneCountryOptions = (
  allowedCountryIsos?: readonly string[],
): ReadonlyArray<PhoneCountryOption> => {
  if (!allowedCountryIsos?.length) {
    return PHONE_COUNTRY_OPTIONS;
  }

  const allowedCountryIsoSet = new Set(
    allowedCountryIsos.map((countryIso) => countryIso.trim().toUpperCase()),
  );

  return Object.freeze(
    PHONE_COUNTRY_OPTIONS.filter((country) => allowedCountryIsoSet.has(country.iso2)),
  );
};

const PRIMARY_DIAL_CODE_ISO: Readonly<Record<string, string>> = {
  '+1': 'US',
  '+44': 'GB',
  '+262': 'RE',
  '+358': 'FI',
  '+599': 'CW',
};

const normalizeCountryIso = (countryIso?: string | null): string | null => {
  const normalizedCountryIso = countryIso?.trim().toUpperCase() ?? '';

  return normalizedCountryIso.length === 2 ? normalizedCountryIso : null;
};

const normalizePhone = (rawPhone: string): string => {
  const withoutExtension = rawPhone.replace(/\s*(?:ext\.?|extension|x)\s*\d+\s*$/i, '');
  const digitsAndPlusOnly = withoutExtension.trim().replace(/[^\d+]/g, '');
  const normalizedPlus = digitsAndPlusOnly.startsWith('00')
    ? `+${digitsAndPlusOnly.slice(2)}`
    : digitsAndPlusOnly;

  if (!normalizedPlus.includes('+')) {
    return normalizedPlus;
  }

  return `+${normalizedPlus.replace(/\+/g, '')}`;
};

export const findPhoneCountryByPrefix = (
  rawPhone: string,
  options: SplitPhoneNumberOptions = {},
): PhoneCountryOption | null => {
  const normalizedPhone = normalizePhone(rawPhone);
  const preferredCountryIso = normalizeCountryIso(options.preferredCountryIso);

  if (!normalizedPhone) {
    return null;
  }

  const matchingCountries = PHONE_COUNTRY_OPTIONS.filter((country) =>
    normalizedPhone.startsWith(country.phone),
  ).sort((left, right) => right.phone.length - left.phone.length);

  if (matchingCountries.length === 0) {
    return null;
  }

  const longestPrefixLength = matchingCountries[0]?.phone.length ?? 0;
  const longestPrefixMatches = matchingCountries.filter(
    (country) => country.phone.length === longestPrefixLength,
  );

  if (preferredCountryIso) {
    const preferredMatch = longestPrefixMatches.find(
      (country) => country.iso2 === preferredCountryIso,
    );

    if (preferredMatch) {
      return preferredMatch;
    }
  }

  const primaryIso = PRIMARY_DIAL_CODE_ISO[longestPrefixMatches[0]?.phone ?? ''];
  const primaryMatch = primaryIso
    ? longestPrefixMatches.find((country) => country.iso2 === primaryIso)
    : undefined;

  return primaryMatch ?? longestPrefixMatches[0] ?? null;
};

export const splitPhoneNumber = (
  rawPhone: string,
  options: SplitPhoneNumberOptions = {},
): SplitPhoneNumberResult => {
  const normalizedPhone = normalizePhone(rawPhone);

  if (!normalizedPhone) {
    return {
      phoneCountry: null,
      phoneNumber: '',
    };
  }

  const phoneCountry = findPhoneCountryByPrefix(normalizedPhone, options);
  const phoneNumber = (
    phoneCountry ? normalizedPhone.slice(phoneCountry.phone.length) : normalizedPhone
  ).replace(/\D/g, '');

  return {
    phoneCountry,
    phoneNumber,
  };
};
